import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import HubspotForm from './HubSpotForm';
import HeroVisual from './HeroVisual';
import ScrollReveal from './ScrollReveal';
import Seo from './Seo';
import { SOLUTIONS, INDUSTRIES } from '../solutions/data';

// Repositioning 2026-09: Qcoda is a governed AI orchestration platform. Software delivery
// is one of several use cases surfaced via /solutions. Home reads as a company overview,
// not a single-product landing page. Copy is scaffolded — refine over subsequent turns.
function Home({ setPage }) {
    return (
        <main>
            <Seo
                title="Qcoda — The Governance Platform for AI Projects"
                description="Qcoda is the governance platform for AI projects. One control plane for orchestrating models, aligning teams, and ledgering every decision — across software delivery, audit, architecture, and research."
                path="/"
            />

            {/* ===== HERO ===== */}
            <section className="site-section-lg">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={10} xl={8}>
                            <ScrollReveal animation="fade-in">
                                <span className="badge-ea mb-4 d-inline-block">Now onboarding design partners</span>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <h1 className="hero-headline mb-4">
    <span className="hero-headline-main">Governance Platform</span>
    <span className="hero-headline-sub">For AI-Assisted Projects</span>
</h1>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={250}>
                                <p className="hero-sub mx-auto mb-4">
                                    Orchestrate <strong>open-source, open-weight, or frontier models</strong> —
                                    on your infrastructure or ours. Align the teams. Ledger every decision.
                                    The same governance follows AI from software delivery through
                                    audit, architecture, and research.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <div className="d-flex gap-3 justify-content-center flex-wrap">
                                    <Link to="/solutions" className="btn-primary-cta">Explore solutions</Link>
                                    <button onClick={() => setPage('contact')} className="btn-secondary-cta">
                                        Talk to us
                                    </button>
                                </div>
                            </ScrollReveal>

                            <ScrollReveal animation="fade-up" delay={500}>
                                <figure className="hero-product-shot">
                                    <img
                                        src="/product/dashboard-concerns.png"
                                        alt="Qcoda dashboard: concerns needing attention across projects with severity, category, and description."
                                        loading="eager"
                                    />
                                    <figcaption className="hero-product-caption">
                                        Every concern, every project — one board-ready view.
                                    </figcaption>
                                </figure>
                            </ScrollReveal>

                            <ScrollReveal animation="fade-up" delay={525}>
                                <figure className="hero-testimonial">
                                    <blockquote className="hero-testimonial-quote">
                                        <p className="hero-testimonial-lead">
                                            &ldquo;QCoda changed the DNA of our company.&rdquo;
                                        </p>
                                        <p className="hero-testimonial-body">
                                            &ldquo;I fed QCoda 300 features, and in a night or two it had
                                            checked, evaluated, aligned, and written full specifications
                                            for every one. For our team, that would have taken months
                                            doing it the old way.&rdquo;
                                        </p>
                                    </blockquote>
                                    <figcaption className="hero-testimonial-attribution">
                                        <span className="hero-testimonial-name">
                                            <a
                                                href="https://www.linkedin.com/in/gwencheylan/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hero-testimonial-link"
                                                aria-label="Gwen Cheylan on LinkedIn"
                                            >
                                                Gwen Cheylan
                                                <svg
                                                    className="hero-testimonial-linkedin"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                >
                                                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.23 0z"/>
                                                </svg>
                                            </a>
                                        </span>
                                        <span className="hero-testimonial-role">
                                            CEO,{' '}
                                            <a
                                                href="https://talential.net/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hero-testimonial-link"
                                            >
                                                Talential
                                            </a>
                                        </span>
                                    </figcaption>
                                </figure>
                            </ScrollReveal>

                            <ScrollReveal animation="fade-up" delay={550}>
                                <div className="hero-value-row">
                                    <div className="hero-value-item">
                                        <div>
                                            <p className="hero-value-title">Governance by default</p>
                                            <p className="hero-value-desc">Every decision ledgered. Every output traceable to its inputs.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div>
                                            <p className="hero-value-title">Orchestration across models</p>
                                            <p className="hero-value-desc">Right model, right task. Open-weight or hosted, in your perimeter.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div>
                                            <p className="hero-value-title">Alignment across the org</p>
                                            <p className="hero-value-desc">Leadership, LOB owners, and operators share one source of truth.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div>
                                            <p className="hero-value-title">Built for scrutiny</p>
                                            <p className="hero-value-desc">Regulated, audited, board-reviewed environments — by design.</p>
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>

                            <HeroVisual />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== BACKED BY ===== */}
            <section id="backed" className="backed-strip" aria-label="Program memberships">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <ScrollReveal animation="fade-up">
                                <p className="backed-label">Backed by</p>
                                <ul className="backed-logos">
                                    <li>
                                        <a
                                            href="https://www.nvidia.com/en-us/startups/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="NVIDIA Inception Program member"
                                        >
                                            <img
                                                src="/partners/nvidia-inception.svg"
                                                alt="NVIDIA Inception Program"
                                                className="backed-logo"
                                                loading="lazy"
                                            />
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://www.microsoft.com/en-us/startups"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Microsoft for Startups member"
                                        >
                                            <img
                                                src="/partners/microsoft-for-startups.svg"
                                                alt="Microsoft for Startups"
                                                className="backed-logo"
                                                loading="lazy"
                                            />
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://aws.amazon.com/startups/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="AWS Activate member"
                                        >
                                            <img
                                                src="/partners/aws-activate.svg"
                                                alt="AWS Activate"
                                                className="backed-logo"
                                                loading="lazy"
                                            />
                                        </a>
                                    </li>
                                </ul>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== WHY QCODA ===== */}
            <section id="why" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-4">
                                    <span className="eyebrow-lead">Why Qcoda exists</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    AI got fast. Accountability did not.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    Every function is racing to adopt AI — engineering, legal, operations,
                                    research, marketing. The tools are powerful. The oversight is missing.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    Leadership sets a direction. Line-of-business owners set constraints.
                                    The people doing the work generate outputs in chat windows no one else
                                    can see. The gaps between those three layers are where risk lives —
                                    misalignment, drift, compliance exposure, decisions no one can reconstruct.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    Qcoda is the platform that closes those gaps — not by slowing AI down,
                                    but by making everything AI does provable.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== PILLARS ===== */}
            <section id="pillars" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">The platform</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    One control plane. Three pillars. Every use case.
                                </h2>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Governance
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        A live ledger of every decision, input, model choice, and human sign-off.
                                        Board-ready evidence, not tribal knowledge.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Orchestration
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Right model for the right task. Route across open-weight and hosted
                                        models, on your infrastructure. Powered by <a href="/llmesh" className="infra-link">LLMesh</a>.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Alignment
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Leadership, LOB owners, and operators share one source of truth.
                                        Nothing ships until intent, constraints, and execution agree.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== SOLUTIONS ===== */}
            <section id="solutions" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">Solutions</span>
                                <h2 className="section-heading mt-3 mb-3">
                                    Governed AI, delivered four ways.
                                </h2>
                                <p className="section-subheading">
                                    Software delivery is one of them — the first we shipped. Every solution
                                    runs on the same platform, with the same alignment thread from
                                    leadership through to the people doing the work.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        {SOLUTIONS.map((s, i) => (
                            <Col md={6} key={s.slug}>
                                <ScrollReveal animation="fade-up" delay={i * 100} className="h-100">
                                    <Link to={`/solutions/${s.slug}`} className="text-decoration-none">
                                        <div className="glass-card h-100">
                                            <p className="text-dim" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                                                {s.eyebrow}
                                            </p>
                                            <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.5rem', color: 'white' }}>
                                                {s.title}
                                            </h3>
                                            <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.65, marginBottom: '0.75rem' }}>
                                                {s.tagline}
                                            </p>
                                            <p style={{ fontSize: '0.9rem', color: 'rgba(120,170,240,0.9)', margin: 0 }}>
                                                Learn more &rarr;
                                            </p>
                                        </div>
                                    </Link>
                                </ScrollReveal>
                            </Col>
                        ))}
                    </Row>
                    <Row className="justify-content-center text-center mt-4">
                        <Col>
                            <Link to="/solutions" className="btn-secondary-cta">See all solutions</Link>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== INDUSTRIES ===== */}
            <section id="industries" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">Industries</span>
                                <h2 className="section-heading mt-3 mb-3">Built for the highly regulated.</h2>
                                <p className="section-subheading">
                                    Fintech, insuretech, legal, HR, medical, public sector, higher education —
                                    industries where AI without governance is not just risky, it is disqualifying.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-3">
                        {INDUSTRIES.map((ind, i) => (
                            <Col xs={6} md={3} key={ind.slug}>
                                <ScrollReveal animation="fade-up" delay={i * 50}>
                                    <div className="glass-card text-center" style={{ padding: '1.1rem 0.75rem' }}>
                                        <p style={{ margin: 0, fontWeight: 500, color: 'white' }}>{ind.name}</p>
                                    </div>
                                </ScrollReveal>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== ALIGNMENT THREAD ===== */}
            <section id="alignment" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">The alignment thread</span>
                                <h2 className="section-heading mt-3 mb-3">
                                    Three roles. One ledger. Zero surprises.
                                </h2>
                                <p className="section-subheading">
                                    The through-line across every Qcoda solution is the alignment thread —
                                    the connective tissue between the three groups that usually operate in
                                    parallel and only meet at incident review.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Leadership</h3>
                                    <p className="text-dim" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.85rem' }}>
                                        C-suite, board, executive sponsors
                                    </p>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>
                                        Set the direction, the risk posture, and the standards. See the
                                        ledger — not a status update built from memory.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Line-of-business owners</h3>
                                    <p className="text-dim" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.85rem' }}>
                                        Product, legal, compliance, ops
                                    </p>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>
                                        Set the constraints. See them honored in every AI-assisted output.
                                        Raise concerns before they become incidents.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>The people doing the work</h3>
                                    <p className="text-dim" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.85rem' }}>
                                        Engineers, analysts, associates, operators
                                    </p>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>
                                        Execute inside guardrails they can see. Every input, model, and
                                        decision ledgered — so the work defends itself.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== ENTERPRISE / TRUST ===== */}
            <section id="enterprise" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Enterprise-ready</span>
                                </div>
                                <h2 className="section-heading mb-3">
                                    Built for teams that operate under scrutiny.
                                </h2>
                                <p className="section-subheading">
                                    Deployment models, isolation postures, and controls designed for
                                    organizations where AI adoption is a board-level conversation.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Operational privacy by design
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Run Qcoda in your perimeter on open-weight models you host via <a href="/llmesh" className="infra-link">LLMesh</a>.
                                        Or use the same governance over hosted endpoints (Azure OpenAI, AWS Bedrock).
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Multi-org, multi-tenant
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Org-scoped roles, isolated workspaces, and separated inference environments, with dedicated compute available on request.
                                        Suitable for enterprise business units, agencies, and consultancies.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Configurable quality gates
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Thresholds for alignment, evidence coverage, human review, and
                                        risk tolerance. Nothing ships until it meets your standards.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center text-center mt-4">
                        <Col lg={8}>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <button onClick={() => setPage('contact')} className="btn-primary-cta">
                                    Talk to us &rarr;
                                </button>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== INVESTOR ===== */}
            <section className="site-section investor-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={7} xl={6}>
                            <ScrollReveal animation="scale-in">
                                <div className="glass-card-lg text-center">
                                    <h2 className="section-heading mb-3">For investors</h2>
                                    <p className="section-subheading mb-4">
                                        Qcoda is building the control plane for enterprise AI —
                                        governance, orchestration, and alignment across every function
                                        adopting AI at work. Currently onboarding design partners across
                                        regulated verticals, with a priced round targeted for late 2026.
                                    </p>
                                    <button onClick={() => setPage('contact', 'investor')} className="btn-primary-cta">
                                        Let's talk
                                    </button>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== FAQ ===== */}
            <section className="site-section" id="faq" aria-labelledby="faq-heading">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-4">
                                    <span className="eyebrow-quiet">Frequently asked</span>
                                </div>
                                <h2 id="faq-heading" className="section-heading mb-4 text-center">
                                    Common questions about Qcoda.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <dl className="faq-list">
                                    <dt className="faq-question fw-bold mt-4 mb-2" style={{ fontSize: '1.1rem', color: 'white' }}>
                                        What is Qcoda in one sentence?
                                    </dt>
                                    <dd className="faq-answer text-muted-white" style={{ lineHeight: 1.7 }}>
                                        Qcoda is the governed AI orchestration platform — the control plane
                                        for deploying, governing, and orchestrating AI across your business.
                                    </dd>

                                    <dt className="faq-question fw-bold mt-4 mb-2" style={{ fontSize: '1.1rem', color: 'white' }}>
                                        Who is it for?
                                    </dt>
                                    <dd className="faq-answer text-muted-white" style={{ lineHeight: 1.7 }}>
                                        Organizations adopting AI in environments where oversight matters —
                                        regulated verticals (fintech, insuretech, legal, HR, medical, public
                                        sector, higher ed), enterprises with board-level AI scrutiny, and
                                        professional services firms whose deliverables must be defensible.
                                    </dd>

                                    <dt className="faq-question fw-bold mt-4 mb-2" style={{ fontSize: '1.1rem', color: 'white' }}>
                                        Is this only for software engineering?
                                    </dt>
                                    <dd className="faq-answer text-muted-white" style={{ lineHeight: 1.7 }}>
                                        No. Software delivery is the first solution we shipped. Architecture
                                        acceleration, audit and compliance review, and research and campaign
                                        planning share the same platform. See <Link to="/solutions" className="infra-link">Solutions</Link>.
                                    </dd>

                                    <dt className="faq-question fw-bold mt-4 mb-2" style={{ fontSize: '1.1rem', color: 'white' }}>
                                        Does it require sending data to a third-party AI?
                                    </dt>
                                    <dd className="faq-answer text-muted-white" style={{ lineHeight: 1.7 }}>
                                        No. Inference can run entirely inside your perimeter via <Link to="/llmesh" className="infra-link">LLMesh</Link> on
                                        open-weight models you host. Hosted endpoints are optional.
                                    </dd>

                                    <dt className="faq-question fw-bold mt-4 mb-2" style={{ fontSize: '1.1rem', color: 'white' }}>
                                        Where does it integrate?
                                    </dt>
                                    <dd className="faq-answer text-muted-white" style={{ lineHeight: 1.7 }}>
                                        Into the tools each function already uses — IDEs and PM systems for
                                        engineering, document workflows for legal and audit, research and
                                        campaign platforms for comms and strategy. Inference-side, with
                                        private or hosted LLM endpoints.
                                    </dd>

                                    <dt className="faq-question fw-bold mt-4 mb-2" style={{ fontSize: '1.1rem', color: 'white' }}>
                                        How do I engage today?
                                    </dt>
                                    <dd className="faq-answer text-muted-white" style={{ lineHeight: 1.7 }}>
                                        Design partner program is open. Reach out below.
                                    </dd>
                                </dl>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== FINAL CTA ===== */}
            <section className="site-section-lg" id="waitlist">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={6}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-4">Become a design partner</h2>
                                <div className="form-waitlist mb-4">
                                    <HubspotForm
                                        portalId="245090272"
                                        formId="efdfa5a6-a51c-46fe-a225-a2d1dab628dd"
                                        pageName="Design Partner"
                                        submitLabel="Request design partner call"
                                        successMessage="Got it — we'll reach out to see if we're a fit."
                                        fields={[
                                            { name: 'firstname', label: 'First name', type: 'text', required: true, placeholder: 'Jane' },
                                            { name: 'company', label: 'Company', type: 'text', required: true, placeholder: 'Acme Corp' },
                                            { name: 'email', label: 'Work email', type: 'email', required: true, placeholder: 'you@company.com' },
                                            { name: 'message', label: 'Which solution area is most relevant?', type: 'textarea', required: false, placeholder: 'Software delivery, architecture, audit, research — or something else?' },
                                        ]}
                                    />
                                </div>
                                <p className="text-dim" style={{ fontSize: '0.9rem' }}>
                                    Or <button
                                        onClick={() => setPage('contact')}
                                        className="bg-transparent border-0 p-0"
                                        style={{ color: 'rgba(120, 170, 240, 0.9)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.9rem' }}
                                    >
                                        talk to us directly
                                    </button> if you'd prefer.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default Home;
